import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { DataService } from '../../services/data.service';

/**
 * NHS Agreement Guard — protects NHS users who haven't accepted terms.
 *
 * Redirects to `/nhs/account/agreement` only when localStorage
 * `nhs_match_status` or `nhs_origin` is `new-account` and the user has
 * `nhs_agreement` not yet set to 1. Matched / mismatch flows use other screens.
 *
 * Non-NHS users (login_service !== 'nhs') always pass.
 * Returns true if allowed, UrlTree if redirect needed.
 */
export const nhsAgreementGuard: CanActivateFn = (route, state) => {
  const data = inject(DataService);
  const router = inject(Router);

  const user: any = (data as any).user ?? null;

  // Not logged in yet (e.g. direct /accesscodes hit before fakesplash pulse) — let AuthGuard handle it.
  // We only block NHS users who are logged in but haven't agreed.
  if (!user) {
    // Try to read from storage fallback (DataService hydrates async, guard may run early)
    try {
      const raw = localStorage.getItem('user') || localStorage.getItem('userData') || sessionStorage.getItem('user');
      if (raw) {
        const parsed = JSON.parse(raw);
        if (needsNhsAccountAgreementScreen(parsed)) {
          return redirectForNhs(router);
        }
      }
    } catch {}
    return true;
  }

  if (!needsNhsAccountAgreementScreen(user)) {
    return true;
  }

  return redirectForNhs(router);
};

/**
 * True when the NHS new-account agreement form should be shown.
 * Callback status `new-account` and an unsigned `nhs_agreement` are enough —
 * a missing user or a login_service that has not been flipped to `nhs` yet
 * must not skip the form.
 */
export function needsNhsAccountAgreementScreen(
  user: Record<string, unknown> | null | undefined,
): boolean {
  if (!isNhsNewAccountFlow()) {
    return false;
  }
  if (!user) {
    return true;
  }
  return !isNhsAgreementAccepted(user);
}

export function isNhsNewAccountFlow(): boolean {
  try {
    const matchStatus = localStorage.getItem('nhs_match_status');
    const origin = localStorage.getItem('nhs_origin');
    return matchStatus === 'new-account' || origin === 'new-account';
  } catch {
    return false;
  }
}

function isNhsAgreementAccepted(user: Record<string, unknown>): boolean {
  const agreement = user.nhs_agreement ?? user.nhsAgreement ?? user.nhs_agree;
  return agreement === 1 || agreement === '1' || agreement === true || agreement === 'true';
}

function redirectForNhs(router: Router) {
  return router.createUrlTree(['/nhs/account/agreement']);
}
